/*
 * cn-emu.js — CloudNine structural emulation (AUTHORED BY BAYSLY, NOT DERIVED)
 *
 * Purpose: reproduce the STRUCTURAL behaviours that CloudNine's merchant theme
 * JS exhibits, so the tracker-24 battery runs against a comparable host. Every
 * line here is written from scratch against the structural fingerprint recorded
 * in docs/spikes/cloudnine_emulation_battery_2026-08-18.md §2. No CloudNine
 * source was copied; the reproduced facts are behaviours, not code.
 *
 * Structural facts reproduced (fingerprint → mechanism):
 *   F-J1  deferred merchant script, loaded after Dawn core, single
 *         DOMContentLoaded entry point
 *   F-J2  1s setTimeout poll waiting for the Kaching drawer root to exist
 *         (`#kaching-cart, #cart-drawer`), because Kaching assigns the
 *         canonical id at runtime
 *   F-J3  MutationObserver {childList:true, subtree:true} whose host resolves
 *         to document.body (the merchant's selector list names UpCart nodes
 *         that do not exist on this store, so the || document.body fallback is
 *         what actually runs) — THE load-bearing delta: every DOM insertion we
 *         make re-enters third-party JS
 *   F-J4  per-mutation scan over Kaching cart-item classes, including
 *         shadowRoot traversal
 *   F-J5  a second MutationObserver that RELOCATES a PDP node into a gallery
 *         container (a foreign-node-moving observer on the product surface)
 *   F-J6  jQuery-style carousel init pass on DOMContentLoaded
 */
(function () {
  'use strict';

  var LOG = false; // set true to trace during a battery run
  function trace() { if (LOG && window.console) console.log.apply(console, arguments); }

  // ── F-J4: the per-mutation scan ─────────────────────────────────────────
  // Structurally equivalent to the merchant's hoodie-quantity tally: read a
  // Kaching cart-item title, walk to its quantity input, sum, including through
  // any open shadow root. Deliberately does the same amount of DOM work.
  var TITLE_SEL = '.kaching-cart-item__title';
  var QTY_SEL   = '.kaching-cart-item__quantity-input';
  var scanCount = 0;

  function tallyTrackedUnits() {
    var total = 0;
    scanCount++;
    try {
      document.querySelectorAll(TITLE_SEL).forEach(function (titleEl) {
        var text = (titleEl.textContent || '').trim().toLowerCase();
        if (text.indexOf('hoodie') < 0) return;
        var scope = titleEl.closest('[class*="kaching-cart-item__content"]') || titleEl.parentElement;
        var qty = scope && scope.querySelector(QTY_SEL);
        if (qty) total += parseInt(qty.value, 10) || 0;
      });
      // shadowRoot traversal — same cost shape as the merchant's pass
      document.querySelectorAll('*').forEach(function (el) {
        if (!el.shadowRoot) return;
        var t = el.shadowRoot.querySelector(TITLE_SEL);
        if (!t) return;
        var q = el.shadowRoot.querySelector(QTY_SEL);
        if (q) total += parseInt(q.value, 10) || 0;
      });
    } catch (e) { /* merchant code swallows; so does this */ }
    applyTierClasses(total);
    return total;
  }

  // Toggles state classes on a checkout-adjacent region, as the merchant does.
  function applyTierClasses(total) {
    try {
      var s1 = document.querySelector('.cn-emu-precheckout .cn-emu-step.step1');
      var s2 = document.querySelector('.cn-emu-precheckout .cn-emu-step.step2');
      if (s1) s1.classList.toggle('active', total >= 1);
      if (s2) s2.classList.toggle('active', total >= 2);
    } catch (e) {}
  }

  // ── F-J3: the body-wide subtree observer ────────────────────────────────
  var bodyObserver = null;
  function startUnitWatcher() {
    if (bodyObserver) return;
    var host = document.querySelector('#UpcartPopup, #upcart, #UpCart, #upCart') || document.body;
    tallyTrackedUnits();
    bodyObserver = new MutationObserver(function () { tallyTrackedUnits(); });
    bodyObserver.observe(host, { childList: true, subtree: true });
    trace('cn-emu: unit watcher on', host.nodeName, '(subtree)');
  }

  // ── F-J2: poll for the runtime-assigned Kaching drawer root ─────────────
  var pollTries = 0;
  function waitForDrawerRoot() {
    if (document.querySelector('#kaching-cart, #cart-drawer')) { startUnitWatcher(); return; }
    if (++pollTries > 60) { startUnitWatcher(); return; } // give up, still watch
    setTimeout(waitForDrawerRoot, 1000);
  }

  // ── F-J5: the node-relocating observer on the product surface ───────────
  function startGalleryRelocator() {
    var tagline = document.querySelector('.cn-emu-variant-tagline');
    if (!tagline) return;
    new MutationObserver(function () {
      var target = document.querySelector('.cn-emu-media-gallery');
      if (target && !target.contains(tagline)) target.appendChild(tagline);
    }).observe(document.body, { childList: true, subtree: true });
    trace('cn-emu: gallery relocator armed');
  }

  // ── F-J6: carousel/reveal init pass ─────────────────────────────────────
  function initCarousels() {
    var els = document.querySelectorAll('.cn-emu-carousel');
    for (var i = 0; i < els.length; i++) {
      els[i].classList.add('cn-emu-carousel--ready');
      els[i].setAttribute('data-cn-emu-slides', String(els[i].children.length));
    }
    var reveal = document.querySelectorAll('.cn-emu-reveal');
    if (reveal.length && 'IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { if (e.isIntersecting) e.target.classList.add('cn-emu-shown'); });
      }, { rootMargin: '0px 0px -10% 0px' });
      reveal.forEach(function (n) { io.observe(n); });
    }
  }

  // ── F-J1: single DOMContentLoaded entry ─────────────────────────────────
  document.addEventListener('DOMContentLoaded', function () {
    initCarousels();
    startGalleryRelocator();
    waitForDrawerRoot();
    window.__cnEmu = {
      scans: function () { return scanCount; },
      observerHost: function () { return bodyObserver ? 'body-subtree' : 'none'; },
      tally: tallyTrackedUnits
    };
    trace('cn-emu: ready');
  });
})();
